import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, Power, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, errMsg } from "@/lib/api";

export function useAgent(key) {
  const [agent, setAgent] = useState(null);
  const load = useCallback(() => api.get(`/agents/${key}`).then((r) => setAgent(r.data)), [key]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!agent?.running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [agent?.running, load]);
  const act = async (a) => {
    try { const r = await api.post(`/agents/${key}/${a}`); setAgent(r.data); toast.success(a === "enable" ? "Agent enabled" : "Agent paused"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveSettings = async (s) => {
    try { const r = await api.put(`/agents/${key}/settings`, s); setAgent(r.data); toast.success("Settings saved (audit logged)"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return { agent, load, act, saveSettings };
}

export function AgentStatus({ agent, testid }) {
  if (!agent) return null;
  if (agent.running) return <Badge className="bg-blue-600 hover:bg-blue-600 gap-1" data-testid={testid}><Loader2 className="h-3 w-3 animate-spin" /> Working</Badge>;
  return agent.enabled
    ? <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid={testid}>Enabled</Badge>
    : <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600" data-testid={testid}>Paused</Badge>;
}

export function AgentControls({ agent, act, onRun, runLabel = "Run Now", prefix }) {
  if (!agent) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {agent.enabled ? (
        <Button variant="outline" size="sm" onClick={() => act("pause")} data-testid={`${prefix}-pause-btn`}><Pause className="h-4 w-4 mr-1" /> Pause</Button>
      ) : (
        <Button variant="outline" size="sm" onClick={() => act("enable")} data-testid={`${prefix}-enable-btn`}><Power className="h-4 w-4 mr-1" /> Enable</Button>
      )}
      {onRun && (
        <Button size="sm" onClick={onRun} disabled={agent.running} data-testid={`${prefix}-run-btn`}>
          {agent.running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />} {runLabel}
        </Button>
      )}
    </div>
  );
}

const LEVEL = {
  error: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-900",
  audit: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-400 dark:border-indigo-900",
  action: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900",
  info: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

export function ActivityLog({ agentKey, refreshKey }) {
  const [logs, setLogs] = useState([]);
  const [level, setLevel] = useState("");
  useEffect(() => { api.get(`/agents/${agentKey}/logs`, { params: { level } }).then((r) => setLogs(r.data)); }, [agentKey, level, refreshKey]);
  return (
    <div data-testid={`${agentKey}-activity-log`}>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["", "All"], ["action", "Actions"], ["error", "Errors"], ["audit", "Audit"], ["info", "Info"]].map(([v, l]) => (
          <Button key={l} size="sm" variant={level === v ? "default" : "outline"} onClick={() => setLevel(v)} data-testid={`${agentKey}-log-filter-${l.toLowerCase()}`}>{l}</Button>
        ))}
      </div>
      {logs.length === 0 ? <div className="text-sm text-muted-foreground py-8 text-center">No activity yet.</div> : (
        <div className="divide-y">
          {logs.map((l) => (
            <div key={l.id} className="py-2.5 flex gap-3 items-start text-sm">
              <span className={`text-[10px] uppercase font-bold tracking-wider border rounded px-1.5 py-0.5 shrink-0 ${LEVEL[l.level] || LEVEL.info}`}>{l.level}</span>
              <div className="min-w-0 flex-1">
                <div className="break-words">{l.message}</div>
                {l.meta && Object.keys(l.meta).length > 0 && <div className="text-xs text-muted-foreground font-mono break-all mt-0.5">{JSON.stringify(l.meta).slice(0, 220)}</div>}
              </div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StatTile({ label, value, tone = "", testid }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className={`font-heading font-bold text-2xl num mt-1 ${tone}`}>{value ?? "—"}</div>
    </div>
  );
}

export function ConnBadge({ state }) {
  const map = { connected: ["Connected", "bg-emerald-600"], not_configured: ["Setup Required", "bg-slate-500"], error: ["Error", "bg-rose-600"], draft_only: ["Draft only", "bg-amber-600"] };
  const [l, c] = map[state] || [state, "bg-slate-500"];
  return <Badge className={`${c} hover:${c} text-white`}>{l}</Badge>;
}

export function AuthImg({ path, className, alt = "" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (!path) return;
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(null));
    return () => url && URL.revokeObjectURL(url);
  }, [path]);
  return src ? <img src={src} alt={alt} className={className} /> : <div className={`${className} bg-muted animate-pulse`} />;
}
